import React, { useMemo, useState, useEffect } from 'react';
import { Building2, IndianRupee, BarChart3, Users, Mail, Upload, ShieldCheck, KeyRound, Percent, FileText, LogOut, Menu, X } from 'lucide-react';
import ChangePasswordDialog from './ChangePasswordDialog';

const NAV_ITEMS = [
  { key: 'dashboard',  label: 'Dashboard',    icon: BarChart3,   roles: ['admin', 'plant_manager', 'viewer'] },
  { key: 'entry',      label: 'Data Entry',   icon: Upload,      roles: ['admin', 'plant_manager', 'operator'] },
  { key: 'plants',     label: 'Plant Master', icon: Building2,   roles: ['admin'] },
  { key: 'rates',      label: 'Manage Rates', icon: IndianRupee, roles: ['admin', 'plant_manager'] },
  { key: 'co2_losses', label: 'CO₂ Losses',   icon: Percent,     roles: ['admin', 'plant_manager'] },
  { key: 'users',      label: 'Users',        icon: Users,       roles: ['admin'] },
  { key: 'approvals',  label: 'Approvals',    icon: ShieldCheck, roles: ['admin'] },
  { key: 'audit_log',  label: 'Audit Log',    icon: FileText,    roles: ['admin'] },
  { key: 'emails',     label: 'Emails',       icon: Mail,        roles: ['admin', 'plant_manager'] },
];

/**
 * Responsive navigation:
 * - Desktop (>=md): Vertical hover-expanding sidebar. Collapsed=64px, Expanded=224px.
 * - Mobile (<md): Slide-in drawer triggered by the hamburger in the mobile top bar.
 */
const SideNav = ({ user, permissions, activeView, setActiveView, onLogout, token }) => {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const expanded = pinned || hovered || mobileOpen;

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const visibleNavItems = useMemo(() => {
    const permsLoaded = permissions && Object.keys(permissions).length > 0;
    return NAV_ITEMS.filter(item =>
      permsLoaded ? permissions[item.key] === true : item.roles.includes(user?.role)
    );
  }, [permissions, user?.role]);

  const handleNavClick = (key) => {
    setActiveView(key);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile top bar (visible <md only) */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white shadow-sm border-b border-[#156ACA]/20 flex items-center justify-between px-3 h-14"
        data-testid="mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="p-2 -ml-1 rounded-md text-[#156ACA] hover:bg-[#E8F2FC] active:bg-[#E8F2FC]"
          aria-label="Open navigation"
          data-testid="mobile-nav-open-btn"
        >
          <Menu className="h-6 w-6" />
        </button>
        <img src="/assets/logo.png" alt="Premier Carbonic" className="h-8" />
        <button
          type="button"
          onClick={onLogout}
          className="p-2 -mr-1 rounded-md text-rose-600 hover:bg-rose-50 active:bg-rose-50"
          aria-label="Logout"
          data-testid="mobile-logout-btn"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
          data-testid="mobile-nav-backdrop"
        />
      )}

      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`fixed top-0 left-0 h-screen z-50 bg-white shadow-xl border-r-2 border-[#156ACA] flex flex-col transition-all duration-200
          ${expanded ? 'w-64 md:w-56' : 'w-16'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        data-testid="side-nav"
      >
        {/* Logo + pin toggle (desktop) or close (mobile) */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200">
          <img src="/assets/logo.png" alt="Premier Carbonic" className="h-9 flex-shrink-0" />
          {mobileOpen && (
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="md:hidden p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close navigation"
              data-testid="mobile-nav-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {expanded && !mobileOpen && (
            <button
              type="button"
              onClick={() => setPinned(p => !p)}
              className={`hidden md:inline-flex p-1.5 rounded-md transition-colors ${pinned ? 'bg-[#156ACA] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              title={pinned ? 'Auto-collapse on mouse leave' : 'Pin sidebar open'}
              data-testid="sidenav-pin-toggle"
            >
              <Menu className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-2">
          {visibleNavItems.map(item => {
            const Icon = item.icon;
            const isActive = activeView === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.key)}
                className={`w-full flex items-center px-4 py-3 min-h-[44px] transition-colors text-left whitespace-nowrap overflow-hidden ${
                  isActive
                    ? 'bg-[#156ACA] text-white font-semibold'
                    : 'text-gray-700 hover:bg-[#E8F2FC] hover:text-[#156ACA] active:bg-[#E8F2FC]'
                }`}
                data-testid={`nav-${item.key}`}
                title={item.label}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${expanded ? 'mr-3' : ''}`} />
                <span className={`text-sm transition-opacity duration-150 ${expanded ? 'opacity-100' : 'opacity-0'}`}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User + actions */}
        <div className="border-t border-gray-200 px-2 py-3 space-y-1">
          {expanded && (
            <div className="px-2 pb-2">
              <p className="text-sm font-semibold text-gray-900 truncate">{user?.username}</p>
              <p className="text-xs text-gray-500 capitalize truncate">{user?.role?.replace('_', ' ')}</p>
            </div>
          )}
          <button
            onClick={() => { setPwdOpen(true); setMobileOpen(false); }}
            className="w-full flex items-center px-3 py-2 min-h-[44px] rounded-lg text-gray-700 hover:bg-[#E8F2FC] hover:text-[#156ACA] transition-colors"
            title="Change password"
            data-testid="open-change-password-btn"
          >
            <KeyRound className={`h-4 w-4 flex-shrink-0 ${expanded ? 'mr-3' : ''}`} />
            <span className={`text-sm transition-opacity ${expanded ? 'opacity-100' : 'opacity-0'}`}>Change Password</span>
          </button>
          <button
            onClick={onLogout}
            className="w-full flex items-center px-3 py-2 min-h-[44px] rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
            data-testid="logout-btn"
            title="Logout"
          >
            <LogOut className={`h-4 w-4 flex-shrink-0 ${expanded ? 'mr-3' : ''}`} />
            <span className={`text-sm transition-opacity ${expanded ? 'opacity-100' : 'opacity-0'}`}>Logout</span>
          </button>
        </div>
      </aside>

      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} token={token} />
    </>
  );
};

export default SideNav;
